import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '../layouts/DashboardLayout';
import PageHeader from '../components/layout/PageHeader';
import { departmentsApi } from '../lib/api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Plus, PencilSimple, Trash, CircleNotch, Buildings } from '@phosphor-icons/react';
import { formatDate, formatApiErrorDetail } from '../lib/utils';
import { toast } from 'sonner';

export default function DepartmentsPage() {
  const { t } = useTranslation();
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDept, setEditingDept] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const loadDepartments = useCallback(async () => {
    try {
      const response = await departmentsApi.list();
      setDepartments(response.data);
    } catch (error) {
      toast.error('Failed to load departments');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDepartments();
  }, [loadDepartments]);

  const handleOpenDialog = (dept = null) => {
    if (dept) {
      setEditingDept(dept);
      setFormData({
        name: dept.name || '',
        description: dept.description || '',
      });
    } else {
      setEditingDept(null);
      setFormData({
        name: '',
        description: '',
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingDept) {
        await departmentsApi.update(editingDept.id, formData);
        toast.success('Department updated successfully');
      } else {
        await departmentsApi.create(formData);
        toast.success('Department created successfully');
      }
      setDialogOpen(false);
      loadDepartments();
    } catch (error) {
      toast.error(formatApiErrorDetail(error.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this department?')) return;
    try {
      await departmentsApi.delete(id);
      toast.success('Department deleted successfully');
      loadDepartments();
    } catch (error) {
      toast.error('Failed to delete department');
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="departments-page">
        <PageHeader
          title={t('departments.title')}
          description={t('departments.subtitle')}
          actions={(
            <Button onClick={() => handleOpenDialog()} data-testid="add-department-btn">
              <Plus className="mr-2 h-4 w-4" />
              {t('departments.addDepartment')}
            </Button>
          )}
        />

        {/* Departments Table */}
        <Card className="bg-white border-zinc-200 shadow-sm">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-200 hover:bg-transparent">
                  <TableHead className="text-zinc-500">{t('departments.title')}</TableHead>
                  <TableHead className="text-zinc-500">{t('common.description')}</TableHead>
                  <TableHead className="text-zinc-500">{t('common.status')}</TableHead>
                  <TableHead className="text-zinc-500">{t('common.created')}</TableHead>
                  <TableHead className="text-zinc-500 text-right">{t('common.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-zinc-400">
                      <CircleNotch className="w-6 h-6 animate-spin mx-auto" />
                    </TableCell>
                  </TableRow>
                ) : departments.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-zinc-400">
                      {t('departments.noDepartments')}
                    </TableCell>
                  </TableRow>
                ) : (
                  departments.map((dept) => (
                    <TableRow key={dept.id} className="border-zinc-200 hover:bg-zinc-50" data-testid={`dept-row-${dept.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-md bg-purple-50 flex items-center justify-center">
                            <Buildings weight="fill" className="w-5 h-5 text-purple-500" />
                          </div>
                          <span className="text-zinc-900 font-medium">{dept.name}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-zinc-500">{dept.description || '-'}</TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 rounded text-xs font-medium border ${
                          dept.is_active !== false
                            ? 'bg-green-50 text-green-600 border-green-200'
                            : 'bg-red-50 text-red-600 border-red-200'
                        }`}>
                          {dept.is_active !== false ? t('common.active') : t('common.inactive')}
                        </span>
                      </TableCell>
                      <TableCell className="text-zinc-500">{formatDate(dept.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleOpenDialog(dept)}
                            className="text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100"
                            data-testid={`edit-dept-${dept.id}`}
                          >
                            <PencilSimple className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(dept.id)}
                            className="text-red-500 hover:text-red-600 hover:bg-red-50"
                            data-testid={`delete-dept-${dept.id}`}
                          >
                            <Trash className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-white border-zinc-200 text-zinc-900 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-zinc-900 font-heading">
                {editingDept ? t('departments.editDepartment') : t('departments.addDepartment')}
              </DialogTitle>
              <DialogDescription className="text-zinc-500">
                {editingDept ? t('departments.editDesc') : t('departments.addDesc')}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="space-y-2">
                <Label className="text-zinc-700">Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white border-zinc-300 text-zinc-900"
                  data-testid="dept-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-700">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-white border-zinc-300 text-zinc-900"
                  data-testid="dept-description-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="border-zinc-300 text-zinc-700 hover:bg-zinc-100"
                data-testid="cancel-dept-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !formData.name}
                className="bg-brand-600 hover:bg-brand-700 text-white"
                data-testid="save-dept-btn"
              >
                {saving ? <CircleNotch className="w-4 h-4 animate-spin" /> : t('common.save')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
